<script setup>
  import { onMounted, ref, computed, watch, nextTick } from 'vue'
import BaseCarousel from '@/components/BaseCarousel.vue'
import InfiniteCarousel from '@/components/InfiniteCarousel.vue'
import LandingNav from '@/components/landing/LandingNav.vue'
import CustomCard from '@/components/CustomCard.vue'
import CustomActionCard from '@/components/CustomActionCard.vue'
import Footer from '@/components/Footer.vue'
import Leaderboard from '@/components/Leaderboard.vue'
import TopLlmsPerIndustryCard from '@/components/TopLlmsPerIndustryCard.vue'
import BaseSelectInput from '@/components/inputs/BaseSelectInput.vue'
import { fetchTopLlmsPerIndustryLeaderboard, fetchEvalRunsAverageLeaderboard, fetchEvalTagsWithLeaderboard, fetchLlms } from '@/services/leaderboard-service'
import { getActiveListings } from '@/services/explore-service'

const mapItems = (items) => {
  return items.map(item => {
    return {
      label: item.llm,
      value: item.average
    }
  })
}

const buildLlmIconMap = (llms) => {
  if (!Array.isArray(llms)) return {};
  return llms.reduce((acc, llm) => {
    const name = llm?.name ? String(llm.name).trim().toLowerCase() : '';
    if (name) {
      acc[name] = llm?.icon || null;
    }
    return acc;
  }, {});
}

const applyLlmIcons = (leaderboard, iconMap) => {
  if (!Array.isArray(leaderboard)) return [];
  const map = iconMap || {};
  return leaderboard.map((item) => {
    const name = item?.llm ? String(item.llm).toLowerCase() : '';
    return {
      ...item,
      llm_icon: map[name] || null,
    };
  });
}

const topLlmsPerIndustryLeaderboard = ref([]);
const isLoadingTopLlmsPerIndustry = ref(false);
const featuredWorks = ref([]);
const carouselItems = ref([]);
const modelData1 = ref([]);
const modelData2 = ref([]);
const evalTagsData = ref([]);
const llmIconByName = ref({});

// Leaderboard 1
const input1Leaderboard1 = ref('');
const input2Leaderboard1 = ref('');
const isUpdatingTypeFromTag1 = ref(false);
const isLoadingLeaderboard1 = ref(false);

// Leaderboard 2
const input1Leaderboard2 = ref('');
const input2Leaderboard2 = ref('');
const isUpdatingTypeFromTag2 = ref(false);
const isLoadingLeaderboard2 = ref(false);

const allTagOptions = computed(() => {
  if (!evalTagsData.value || !Array.isArray(evalTagsData.value)) return [];
  
  const tagsMap = new Map();
  
  evalTagsData.value.forEach(item => {
    if (item.tags && Array.isArray(item.tags)) {
      item.tags.forEach(tag => {
        if (!tagsMap.has(tag.id)) {
          tagsMap.set(tag.id, {
            label: tag.name || String(tag.id),
            value: tag.id,
            originalTag: tag
          });
        }
      });
    }
  });
  
  return Array.from(tagsMap.values());
});

const tagOptions1 = computed(() => {
  if (input2Leaderboard1.value) {
    const selectedTypeOption = allTypes.value.find(type => type.value === input2Leaderboard1.value);
    if (selectedTypeOption && selectedTypeOption.originalItem) {
      const item = selectedTypeOption.originalItem;
      if (item.tags && Array.isArray(item.tags)) {
        return item.tags.map(tag => {
          const existingTag = allTagOptions.value.find(t => t.value === tag.id);
          if (existingTag) {
            return existingTag;
          }
          return {
            label: tag.name || String(tag.id),
            value: tag.id,
            originalTag: tag
          };
        });
      }
    }
    return [];
  } else {
    return allTagOptions.value;
  }
});

const tagOptions2 = computed(() => {
  if (input2Leaderboard2.value) {
    const selectedTypeOption = allTypes.value.find(type => type.value === input2Leaderboard2.value);
    if (selectedTypeOption && selectedTypeOption.originalItem) {
      const item = selectedTypeOption.originalItem;
      if (item.tags && Array.isArray(item.tags)) {
        return item.tags.map(tag => {
          const existingTag = allTagOptions.value.find(t => t.value === tag.id);
          if (existingTag) {
            return existingTag;
          }
          return {
            label: tag.name || String(tag.id),
            value: tag.id,
            originalTag: tag
          };
        });
      }
    }
    return [];
  } else {
    return allTagOptions.value;
  }
});

const allTypes = computed(() => {
  if (!evalTagsData.value || !Array.isArray(evalTagsData.value)) return [];
  
  const typesMap = new Map();
  
  evalTagsData.value.forEach(item => {
    if (item.type && item.id) {
      if (!typesMap.has(item.id)) {
        typesMap.set(item.id, {
          label: item.type,
          value: item.id,
          originalItem: item
        });
      }
    }
  });
  
  return Array.from(typesMap.values());
});

const typeOptions1 = computed(() => {
  return allTypes.value;
});

const typeOptions2 = computed(() => {
  return allTypes.value;
});

watch(input2Leaderboard1, async (newTypeId) => {
  if (isUpdatingTypeFromTag1.value) {
    return;
  }
  
  if (newTypeId) {
    const selectedTypeOption = allTypes.value.find(type => type.value === newTypeId);
    if (selectedTypeOption && selectedTypeOption.originalItem) {
      const item = selectedTypeOption.originalItem;
      if (input1Leaderboard1.value) {
        const isTagInType = item.tags && Array.isArray(item.tags) && 
          item.tags.some(tag => tag.id === input1Leaderboard1.value);
        if (!isTagInType) {
          isUpdatingTypeFromTag1.value = true;
          input1Leaderboard1.value = '';
          await nextTick();
          isUpdatingTypeFromTag1.value = false;
        }
      }
    }
    await loadLeaderboard1();
  } else {
    await loadLeaderboard1();
  }
});

watch(input2Leaderboard2, async (newTypeId) => {
  if (isUpdatingTypeFromTag2.value) {
    return;
  }
  
  if (newTypeId) {
    const selectedTypeOption = allTypes.value.find(type => type.value === newTypeId);
    if (selectedTypeOption && selectedTypeOption.originalItem) {
      const item = selectedTypeOption.originalItem;
      if (input1Leaderboard2.value) {
        const isTagInType = item.tags && Array.isArray(item.tags) && 
          item.tags.some(tag => tag.id === input1Leaderboard2.value);
        if (!isTagInType) {
          isUpdatingTypeFromTag2.value = true;
          input1Leaderboard2.value = '';
          await nextTick();
          isUpdatingTypeFromTag2.value = false;
        }
      }
    }
    await loadLeaderboard2();
  } else {
    await loadLeaderboard2();
  }
});

watch(input1Leaderboard1, async (newTagId) => {
  if (isUpdatingTypeFromTag1.value) {
    if (newTagId && input2Leaderboard1.value) {
      await loadLeaderboard1();
    }
    return;
  }
  
  if (newTagId) {
    let typeWithTag = null;
    if (input2Leaderboard1.value) {
      const currentTypeOption = allTypes.value.find(type => type.value === input2Leaderboard1.value);
      if (currentTypeOption && currentTypeOption.originalItem) {
        const item = currentTypeOption.originalItem;
        if (item.tags && Array.isArray(item.tags) && 
            item.tags.some(tag => tag.id === newTagId)) {
          await loadLeaderboard1();
          return;
        }
      }
    }
    
    typeWithTag = allTypes.value.find(typeOption => {
      const item = typeOption.originalItem;
      if (item && item.tags && Array.isArray(item.tags)) {
        return item.tags.some(tag => tag.id === newTagId);
      }
      return false;
    });
    
    if (typeWithTag) {
      
      isUpdatingTypeFromTag1.value = true;
      input2Leaderboard1.value = typeWithTag.value;
      await nextTick();
      isUpdatingTypeFromTag1.value = false;
      await loadLeaderboard1();
    } else {
      
      input2Leaderboard1.value = '';
      await loadLeaderboard1();
    }
  } else {
    input2Leaderboard1.value = '';
    await loadLeaderboard1();
  }
});

watch(input1Leaderboard2, async (newTagId) => {
  if (isUpdatingTypeFromTag2.value) {
    if (newTagId && input2Leaderboard2.value) {
      await loadLeaderboard2();
    }
    return;
  }
  
  if (newTagId) {
    let typeWithTag = null;
    if (input2Leaderboard2.value) {
      const currentTypeOption = allTypes.value.find(type => type.value === input2Leaderboard2.value);
      if (currentTypeOption && currentTypeOption.originalItem) {
        const item = currentTypeOption.originalItem;
        if (item.tags && Array.isArray(item.tags) && 
            item.tags.some(tag => tag.id === newTagId)) {
          await loadLeaderboard2();
          return;
        }
      }
    }
    
    typeWithTag = allTypes.value.find(typeOption => {
      const item = typeOption.originalItem;
      if (item && item.tags && Array.isArray(item.tags)) {
        return item.tags.some(tag => tag.id === newTagId);
      }
      return false;
    });
    
    if (typeWithTag) {
      
      isUpdatingTypeFromTag2.value = true;
      input2Leaderboard2.value = typeWithTag.value;
      await nextTick();
      isUpdatingTypeFromTag2.value = false;
      await loadLeaderboard2();
    } else {
      
      input2Leaderboard2.value = '';
      await loadLeaderboard2();
    }
  } else {
    input2Leaderboard2.value = '';
    await loadLeaderboard2();
  }
});

const loadLeaderboard1 = async () => {
  isLoadingLeaderboard1.value = true;
  try {
    const tagId = input1Leaderboard1.value || null;
    const typeId = input2Leaderboard1.value || null;
    const leaderboard = await fetchEvalRunsAverageLeaderboard(tagId, typeId);
    modelData1.value = applyLlmIcons(leaderboard, llmIconByName.value);
  } finally {
    isLoadingLeaderboard1.value = false;
  }
};

const loadLeaderboard2 = async () => {
  isLoadingLeaderboard2.value = true;
  try {
    const tagId = input1Leaderboard2.value || null;
    const typeId = input2Leaderboard2.value || null;
    const leaderboard = await fetchEvalRunsAverageLeaderboard(tagId, typeId);
    modelData2.value = applyLlmIcons(leaderboard, llmIconByName.value);
  } finally {
    isLoadingLeaderboard2.value = false;
  }
};

const refreshLeaderboardsWithIcons = () => {
  modelData1.value = applyLlmIcons(modelData1.value, llmIconByName.value);
  modelData2.value = applyLlmIcons(modelData2.value, llmIconByName.value);
};

const mappedFeaturedWorks = computed(() => {
  let raw = [];
  if (Array.isArray(featuredWorks.value)) {
    raw = featuredWorks.value;
  } else if (Array.isArray(featuredWorks.value?.listings)) {
    raw = featuredWorks.value.listings;
  }

  return raw.map((item) => {
    const firstTag =
      Array.isArray(item.tags) && item.tags.length > 0
        ? item.tags[0]
        : null;

    const firstTagName = firstTag ? firstTag.name || firstTag.id : null;
    const firstTagColor = firstTag ? firstTag.display_color || null : null;

    return {
      id: item.id,
      title: 'FEATURED WORK',
      itemTitle: item.name || item.title || '',
      itemId: item.id,
      badge: firstTagName || item.type || '',
      badgeColor: firstTagColor,
      content: item.short_description || '',
      footerText: '',
      imageSrc: item.image || '',
      imageAlt: item.name || '',
      badges: item.badges || [],
      tag: item.tag || null,
      username: item.username || item.seller_username || item.creator_username || item.user?.username || '',
    };
  });
});

onMounted(async () => {
  isLoadingTopLlmsPerIndustry.value = true;
  isLoadingLeaderboard1.value = true;
  isLoadingLeaderboard2.value = true;
  try {
    const llmsPromise = fetchLlms();
    topLlmsPerIndustryLeaderboard.value = await fetchTopLlmsPerIndustryLeaderboard();
    evalTagsData.value = await fetchEvalTagsWithLeaderboard();
    const defaultLeaderboard = await fetchEvalRunsAverageLeaderboard(null, null);
    
    modelData1.value = applyLlmIcons(defaultLeaderboard, llmIconByName.value);
    modelData2.value = applyLlmIcons(defaultLeaderboard, llmIconByName.value);

    const llms = await llmsPromise;
    llmIconByName.value = buildLlmIconMap(llms);
    refreshLeaderboardsWithIcons();
  } finally {
    isLoadingTopLlmsPerIndustry.value = false;
    isLoadingLeaderboard1.value = false;
    isLoadingLeaderboard2.value = false;
  }

  const allFeaturedWorks = await getActiveListings('featured', 10);
  
  let allItems = [];
  if (Array.isArray(allFeaturedWorks)) {
    allItems = allFeaturedWorks;
  } else if (Array.isArray(allFeaturedWorks?.listings)) {
    allItems = allFeaturedWorks.listings;
  }
  
  carouselItems.value = allItems;
  
  const shuffled = [...allItems].sort(() => Math.random() - 0.5);
  featuredWorks.value = shuffled.slice(0, 3);
});
</script>

<template>
  <div class="min-h-screen bg-black">
    <LandingNav />
    <div class="mt-[72px] mx-auto max-w-7xl space-y-6 py-6 px-4 sm:px-6 lg:px-8">
    <div class="flex flex-col h-fit space-y-6 md:space-y-10">
      <div class="flex flex-col md:flex-row md:items-center h-fit gap-4 md:gap-8">
        <div class="uppercase flex items-center text-xl sm:text-2xl tracking-widest bg-gradient-to-br from-white to-white/[0.493] bg-clip-text text-transparent">Real World Benchmarks</div>
        <div class="overflow-hidden md:flex-1 md:px-8">
          <InfiniteCarousel :items="carouselItems" />
        </div>
      </div>
      <div class="flex-1 h-fit text-sm sm:text-base text-gray-200 max-w-7xl">
        Compare frontier AI models on tasks drawn from real occupations in the U.S. SOC system. Choose an industry or detailed occupation and see how models rank on work people actually do.
      </div>
    </div>
    
    <!-- Grid 4 -->
    <div v-if="isLoadingTopLlmsPerIndustry" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
      <TopLlmsPerIndustryCard v-for="n in 4" :key="n" :industry-name="''" :items="[]" :isLoading="true" />
    </div>
    <div v-else-if="topLlmsPerIndustryLeaderboard.length > 0" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
      <TopLlmsPerIndustryCard v-for="topIndustry in topLlmsPerIndustryLeaderboard" :key="topIndustry.industry" :industry-name="topIndustry.industry" :items="mapItems(topIndustry.top_llms)" :isLoading="false" />
    </div>
    
    <!-- Grid 2 -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 ">
      <div class="leaderboard-container rounded-xl">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-[15px] px-[15px] pb-[15px]">
          <BaseSelectInput
            v-model="input1Leaderboard1"
            :options="tagOptions1"
            placeholder="Select an occupation"
            searchable
            clearable
            :loading="isLoadingLeaderboard1"
            id="leaderboard1-occupation"
          />
          <BaseSelectInput
            v-model="input2Leaderboard1"
            :options="typeOptions1"
            placeholder="Select an industry"
            searchable
            clearable
            :loading="isLoadingLeaderboard1"
            id="leaderboard1-industry"
          />
        </div>
        <div class="flex-1 flex justify-center items-center">
          <Leaderboard :modelData="modelData1" :isLoading="isLoadingLeaderboard1" />
        </div>
      </div>
      <div class="leaderboard-container rounded-xl">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-[15px] px-[15px] pb-[15px]">
          <BaseSelectInput
            v-model="input1Leaderboard2"
            :options="tagOptions2"
            placeholder="Select an occupation"
            searchable
            clearable
            :loading="isLoadingLeaderboard2"
            id="leaderboard2-occupation"
          />
          <BaseSelectInput
            v-model="input2Leaderboard2"
            :options="typeOptions2"
            placeholder="Select an industry"
            searchable
            clearable
            :loading="isLoadingLeaderboard2"
            id="leaderboard2-industry"
          />
        </div>
        <div class="flex-1 flex justify-center items-center">
          <Leaderboard :modelData="modelData2" :isLoading="isLoadingLeaderboard2" />
        </div>
      </div>
    </div>
    
    <!-- Grid 1 -->
    <div v-if="mappedFeaturedWorks.length > 0" class="grid grid-cols-1 gap-4  custom-carousel-container rounded-xl">
      <div class="overflow-hidden h-fit">
        <BaseCarousel :slides="mappedFeaturedWorks.length">
          <CustomCard
            v-for="item in mappedFeaturedWorks"
            :key="item.id || item.title"
            :title="item.title"
            :itemTitle="item.itemTitle"
            :itemId="item.itemId"
            :badge="item.badge"
            :badgeColor="item.badgeColor"
            :content="item.content"
            :footerText="item.footerText"
            :imageSrc="item.imageSrc"
            :imageAlt="item.imageAlt"
            :badges="item.badges"
            :tag="item.tag"
            :username="item.username"
          />
        </BaseCarousel>
      </div>
    </div>
    
    <div class="relative">
      <h2 class="uppercase flex items-center text-base sm:text-2xl tracking-widest mb-4 sm:mb-6 md:mb-8 bg-gradient-to-br from-white to-white/[0.493] bg-clip-text text-transparent">By experts from</h2>
      <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 lg:gap-10 items-center justify-center px-4 sm:px-6 md:px-8 lg:px-12">
        <img src="/images/items/university_logo_1.png" alt="Logo 1" class="h-24 sm:h-32 md:h-40 lg:h-48 xl:h-56 w-full object-contain mx-auto">
        <img src="/images/items/university_logo_2.png" alt="Logo 2" class="h-24 sm:h-32 md:h-40 lg:h-48 xl:h-56 w-full object-contain mx-auto p-0.5 sm:p-4 md:p-6 lg:p-8">
        <img src="/images/items/university_logo_3.png" alt="Logo 3" class="h-24 sm:h-32 md:h-40 lg:h-48 xl:h-56 w-full object-contain mx-auto sm:p-4 md:p-6 lg:p-8">
        <img src="/images/items/university_logo_4.png" alt="Logo 4" class="h-24 sm:h-32 md:h-40 lg:h-48 xl:h-56 w-full object-contain mx-auto p-6 lg:p-8">
      </div>
    </div>

        <CustomActionCard
          title="Powered by Datalab"
          content="The Portex Datalab is a unified platform for frontier evals and datasets. Domain experts write novel, blinded evaluations and maintain control over pricing. AI labs use the Datalab to test their models against truly out-of-sample questions."
          imageSrc="/images/items/screenshot_01.png"
          actionText="Launch Datalab"
        />

        <CustomActionCard
          title="Write Evals in Minutes"
          reverse
          content="Portex makes it easy to take your expert domain knowledge and generate custom evaluations that you can offer to model builders on the Datalab."
          imageSrc="/images/items/screenshot_02.png"
          actionText="Create an Eval"
          redirectTo="/register"
        />
  </div>
  </div>
  <Footer />
</template>

<style scoped>

  .custom-carousel-container {
    position: relative;
  border: 1px solid #0F0F0F;
  padding: 1.5rem 1rem;
  }



  .custom-carousel-container > * {
    position: relative;
    z-index: 1;
  }

  .leaderboard-container {
    position: relative;
    padding-top: 15px;
    border: 1px solid #0F0F0F;
  }
  

  .leaderboard-container > * {
    position: relative;
    z-index: 1;
  }
</style>